import React, { useState, useCallback, useMemo } from 'react';
import { ChevronDown, ChevronUp, Copy, Download, FileJson, Check, Code, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useToast } from '@/components/hooks/use-toast';
import { ContractDisplay } from './ContractDisplay';

interface StructuredOutputDisplayProps {
  data: any;
  messageId?: string;
  className?: string;
}

export const StructuredOutputDisplay: React.FC<StructuredOutputDisplayProps> = ({
  data,
  messageId,
  className = ''
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const [viewMode, setViewMode] = useState<'custom' | 'json'>('custom');
  const { toast } = useToast();

  // Detect if this is contract extraction data
  // TODO: make this more robust and support other schemas
  const isContractData = useMemo(() => {
    if (!data || typeof data !== 'object') return false;
    
    // Check for key fields that indicate contract extraction
    const contractIndicators = [
      'contract_type',
      'primary_legal_entity',
      'first_party',
      'second_party',
      'obligations',
      'contract_scope',
      'contract_amount'
    ];
    
    // If at least 3 of these fields are present, treat it as contract data
    const matchingFields = contractIndicators.filter(field => field in data);
    return matchingFields.length >= 3;
  }, [data]);

  // Determine if we have a custom display available
  const hasCustomDisplay = isContractData;

  // Toggle expansion state
  const toggleExpanded = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  // Copy JSON to clipboard
  const handleCopy = useCallback(async () => {
    try {
      const jsonString = JSON.stringify(data, null, 2);
      await navigator.clipboard.writeText(jsonString);
      setCopied(true);
      toast({
        title: "Copied to clipboard",
        description: "Structured data has been copied as JSON.",
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
      toast({
        title: "Copy failed",
        description: "Failed to copy data to clipboard.",
        variant: "destructive",
      });
    }
  }, [data, toast]);

  // Download as JSON file
  const handleDownload = useCallback(() => {
    try {
      const jsonString = JSON.stringify(data, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const filename = `structured-output-${timestamp}.json`;
      
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast({
        title: "Download started",
        description: `Saving as ${filename}`,
      });
    } catch (error) {
      console.error('Failed to download:', error);
      toast({
        title: "Download failed",
        description: "Failed to download structured data.",
        variant: "destructive",
      });
    }
  }, [data, toast]);

  // Get a preview of the data for the collapsed state
  const getDataPreview = () => {
    if (!data || typeof data !== 'object') return 'No data available';
    
    const keys = Object.keys(data);
    const keyCount = keys.length;
    
    // For known schema types, show a more informative preview
    if (data.contract_type || data.party1 || data.party2) {
      return `Contract: ${data.contract_type || 'Unknown'} between ${data.party1?.name || 'Party 1'} and ${data.party2?.name || 'Party 2'}`;
    }
    if (data.invoice_number || data.vendor_name) {
      return `Invoice #${data.invoice_number || 'N/A'} from ${data.vendor_name || 'Unknown vendor'}`;
    }
    if (data.meeting_title || data.attendees) {
      return `Meeting: ${data.meeting_title || 'Untitled'} with ${data.attendees?.length || 0} attendees`;
    }
    if (data.balance_sheet || data.income_statement) {
      return `Financial Statement${data.reporting_period ? ` for ${data.reporting_period}` : ''}`;
    }
    
    // Generic preview showing field count and first few fields
    const firstFields = keys.slice(0, 3).join(', ');
    const moreFields = keyCount > 3 ? ` and ${keyCount - 3} more` : '';
    return `${keyCount} fields: ${firstFields}${moreFields}`;
  };

  if (!data) return null;

  return (
    <div className={`mt-2 w-full ${className}`}>
      <div className="rounded-lg border border-border bg-card/50 shadow-sm">
        {/* Header - Always visible */}
        <div className="flex items-center justify-between p-3 border-b border-border/50">
          <div className="flex items-center gap-2">
            <FileJson className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium text-foreground">Structured Data Extraction</span>
          </div>
          <div className="flex items-center gap-1">
            {/* View mode toggle - only show if custom display is available */}
            {hasCustomDisplay && isExpanded && (
              <div className="flex items-center gap-1 mr-2 border-r pr-2">
                <Button
                  onClick={() => setViewMode('custom')}
                  size="sm"
                  variant={viewMode === 'custom' ? 'secondary' : 'ghost'}
                  className="h-7 px-2 text-xs"
                  title="Custom View"
                >
                  <Eye className="h-3.5 w-3.5" />
                </Button>
                <Button
                  onClick={() => setViewMode('json')}
                  size="sm"
                  variant={viewMode === 'json' ? 'secondary' : 'ghost'}
                  className="h-7 px-2 text-xs"
                  title="JSON View"
                >
                  <Code className="h-3.5 w-3.5" />
                </Button>
              </div>
            )}
            
            {/* Copy button */}
            <Button
              onClick={handleCopy}
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-xs hover:bg-accent"
              title="Copy JSON"
            >
              {copied ? (
                <Check className="h-3.5 w-3.5 text-green-600" />
              ) : (
                <Copy className="h-3.5 w-3.5" />
              )}
            </Button>
            
            {/* Download button */}
            <Button
              onClick={handleDownload}
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-xs hover:bg-accent"
              title="Download JSON"
            >
              <Download className="h-3.5 w-3.5" />
            </Button>
            
            {/* Expand/collapse button */}
            <Button
              onClick={toggleExpanded}
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-xs hover:bg-accent"
              aria-expanded={isExpanded}
              aria-controls={`structured-data-${messageId}`}
            >
              {isExpanded ? 'Hide' : 'Show'}
              {isExpanded ? (
                <ChevronUp className="ml-1 h-3 w-3" />
              ) : (
                <ChevronDown className="ml-1 h-3 w-3" />
              )}
            </Button>
          </div>
        </div>

        {/* Preview when collapsed */}
        {!isExpanded && (
          <div className="p-3 text-sm text-muted-foreground">
            {getDataPreview()}
          </div>
        )}

        {/* Full content when expanded */}
        {isExpanded && (
          <div
            id={`structured-data-${messageId}`}
            className="p-3"
          >
            {/* Show custom display or JSON based on view mode */}
            {hasCustomDisplay && viewMode === 'custom' ? (
              // Custom display for specific schemas
              isContractData ? (
                <ContractDisplay data={data} />
              ) : (
                // Fallback to JSON if no custom display is implemented
                <ScrollArea className="h-96 w-full rounded-md border border-border/50 bg-muted/30">
                  <div className="p-4">
                    <pre className="text-xs font-mono text-foreground whitespace-pre-wrap break-words">
                      <code>{JSON.stringify(data, null, 2)}</code>
                    </pre>
                  </div>
                </ScrollArea>
              )
            ) : (
              // JSON view
              <ScrollArea className="h-96 w-full rounded-md border border-border/50 bg-muted/30">
                <div className="p-4">
                  <pre className="text-xs font-mono text-foreground whitespace-pre-wrap break-words">
                    <code>{JSON.stringify(data, null, 2)}</code>
                  </pre>
                </div>
              </ScrollArea>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default StructuredOutputDisplay;